import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Link, useRouterState } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { useEffect, type MouseEvent, type RefObject } from 'react'

import { SidebarNav } from '@/components/app-shell/sidebar-nav'

/**
 * The navigation rail, below `lg`, as a drawer.
 *
 * A 240px column on a 375px screen is not a compromise, it is two thirds of the screen; below the
 * breakpoint the same tree slides in over the work instead. It is built on the dialog primitive
 * rather than a hand-rolled panel because what a drawer has to get right is exactly what a dialog
 * already does: the focus stays inside it while it is open and returns to the button that opened
 * it, Escape closes it, and the page behind it does not scroll away underneath.
 */
export function NavigationDrawer({
  open,
  onOpenChange,
  returnFocusTo,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The control that opened it. Focus goes back there on close. */
  returnFocusTo?: RefObject<HTMLElement | null>
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  // Closing on the location rather than on a click covers every way out of the drawer, including
  // the ones no link fired: a route guard's redirect, or the browser's Back button.
  useEffect(() => {
    onOpenChange(false)
  }, [pathname, onOpenChange])

  // Tapping the item that is already open changes no location, and leaving the drawer standing
  // over the screen the operator just asked for would read as the tap having missed.
  const closeOnNavigation = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('a')) {
      onOpenChange(false)
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="anim-overlay fixed inset-0 z-50 bg-overlay" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          // Radix returns focus to its own trigger, and this drawer is opened from a button in
          // the top bar rather than from one. Without this, closing it drops the keyboard on the
          // document body and the next Tab starts the page again from the top.
          //
          // Not when the button has gone: crossing to `lg` closes the drawer and hides the
          // button with it, and focusing something that is not rendered focuses nothing.
          onCloseAutoFocus={(event) => {
            const trigger = returnFocusTo?.current
            if (trigger && trigger.offsetParent !== null) {
              event.preventDefault()
              trigger.focus()
            }
          }}
          className="anim-drawer inset-safe-x inset-safe-top inset-safe-bottom fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-sidebar-border bg-sidebar shadow-xl"
        >
          <div className="flex h-14 shrink-0 items-center gap-2 px-3">
            <Link to="/" className="flex min-w-0 items-center gap-2" aria-label="Dashboard">
              <span
                className="flex size-7 shrink-0 items-center justify-center rounded-md bg-brand-fill text-on-brand-fill"
                aria-hidden
              >
                <svg viewBox="0 0 32 32" className="size-4.5" fill="none" stroke="currentColor" strokeWidth={3}>
                  <path d="M8 22V10.5l8 6 8-6V22" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <DialogPrimitive.Title className="truncate text-sm font-semibold text-sidebar-text">
                Microcredit
              </DialogPrimitive.Title>
            </Link>

            <DialogPrimitive.Close
              className="ml-auto flex size-11 shrink-0 items-center justify-center rounded-control text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-sidebar-text"
              aria-label="Close navigation"
            >
              <X className="size-5" />
            </DialogPrimitive.Close>
          </div>

          <div className="flex min-h-0 flex-1 flex-col" onClick={closeOnNavigation}>
            <SidebarNav collapsed={false} size="touch" />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
